import { createSelector } from '@ngrx/store';
import { LoyaltyProgramCategory } from '@rd/types';
import {
  CashBackMembership,
  MembershipType,
  PointsTransferMembership,
} from '@rd/types/membership.type';
import { toObject } from '@rd/utils/array.utils';
import { getMembershipFeatureState } from '../reducers';
import { MembershipState } from '../reducers/membership.reducer';

export const getPreferredPointsTransferMembership = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => {
    const memberships = Object.values(state.memberships.data);
    for (const membership of memberships) {
      if (
        membership.preferred &&
        membership.type === MembershipType.PointsTransfer
      ) {
        return membership;
      }
    }
    return null;
  },
);

export const getPreferredCashBackMembership = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => {
    const memberships = Object.values(state.memberships.data);
    for (const membership of memberships) {
      if (
        membership.preferred === true &&
        membership.type === MembershipType.CashBack &&
        (membership.category === LoyaltyProgramCategory.CashbackToCard ||
          membership.category === LoyaltyProgramCategory.CashbackToAccount ||
          membership.category === LoyaltyProgramCategory.CashbackToPlatform)
      ) {
        return membership;
      }
    }
    return null;
  },
);

export const getCashBackToCardMembership = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => {
    const memberships = Object.values(state.memberships.data);
    for (const membership of memberships) {
      if (
        membership.type === MembershipType.CashBack &&
        membership.category === LoyaltyProgramCategory.CashbackToCard
      ) {
        return membership;
      }
    }
    return null;
  },
);

export const getCashBackToAccountMembership = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => {
    const memberships = Object.values(state.memberships.data);
    for (const membership of memberships) {
      if (
        membership.type === MembershipType.CashBack &&
        membership.category === LoyaltyProgramCategory.CashbackToAccount
      ) {
        return membership;
      }
    }
    return null;
  },
);

export const getCashBackToPlatformMembership = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => {
    const memberships = Object.values(state.memberships.data);
    for (const membership of memberships) {
      if (
        membership.type === MembershipType.CashBack &&
        membership.category === LoyaltyProgramCategory.CashbackToPlatform
      ) {
        return membership;
      }
    }
    return null;
  },
);

export const getMemberships = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => Object.values(state.memberships.data),
);

export const getMembershipsMap = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.memberships.data,
);

export const getCashBackMemberships = createSelector(
  getMemberships,
  (state) =>
    state.filter(
      (membership) => membership.type === MembershipType.CashBack,
    ) as CashBackMembership[],
);

export const getCashBackMembershipsMap = createSelector(
  getCashBackMemberships,
  (cashBackMemberships: CashBackMembership[]) =>
    toObject(cashBackMemberships, 'id'),
);

export const getPointsTransferMemberships = createSelector(
  getMemberships,
  (state) =>
    state.filter(
      (membership) => membership.type === MembershipType.PointsTransfer,
    ) as PointsTransferMembership[],
);

export const getMembershipLoading = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.memberships.loading,
);

export const getMembershipError = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.memberships.error,
);

export const getCreateMembershipsLoading = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.createMembership.loading,
);

export const getCreateMembershipsError = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.createMembership.error,
);

export const getCreateMembershipsSuccess = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.createMembership.success,
);

export const getUpdateMembershipsLoading = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.updateMembership.loading,
);

export const getUpdateMembershipsError = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.updateMembership.error,
);

export const getUpdateMembershipsSuccess = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) => state.updateMembership.success,
);

// combination selectors
export const getCreateOrUpdateMembershipError = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) =>
    state.updateMembership.error || state.createMembership.error,
);

// combination selectors
export const getCreateOrUpdateMembershipLoading = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) =>
    state.updateMembership.loading || state.createMembership.loading,
);

// combination selectors
export const getLinkOrUnlinkMembershipLoading = createSelector(
  getMembershipFeatureState,
  (state: MembershipState) =>
    state.linkMembership.loading || state.unlinkMembership.loading,
);
